import React, { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { FiUsers, FiDollarSign, FiClock, FiCheck, FiTrendingUp, FiRefreshCw } from 'react-icons/fi';
import { db } from '../../services/supabase';
import WarningIcon from '../common/icons/WarningIcon';
import ChartIcon from '../common/icons/ChartIcon';
import GrowthIcon from '../common/icons/GrowthIcon';
import TimeIcon from '../common/icons/TimeIcon';
import LightningIcon from '../common/icons/LightningIcon';
import MoneyIcon from '../common/icons/MoneyIcon';
import BullseyeIcon from '../common/icons/BullseyeIcon';
import SettingsGearIcon from '../common/icons/SettingsGearIcon';

const AdminDashboard = () => {
  const queryClient = useQueryClient();
  
  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const result = await db.orders.getStats();
      return result ?? [];
    },
    refetchInterval: 30000,
  });

  const statsObj = Array.isArray(stats) ? stats[0] || {} : stats || {};

  const { data: eventSettings } = useQuery({
    queryKey: ['event-settings'],
    queryFn: db.eventSettings.getLatest,
    staleTime: 5 * 60 * 1000,
  });

  const { data: ticketStats } = useQuery({
    queryKey: ['ticket-stats'],
    queryFn: db.analytics.getTicketStats,
    refetchInterval: 30000,
  });

  const { data: dailyStats } = useQuery({
    queryKey: ['daily-stats'],
    queryFn: () => db.analytics.getDailyStats(7),
    refetchInterval: 60000,
  });

  const { data: recentOrders } = useQuery({
    queryKey: ['recent-orders'],
    queryFn: async () => {
      const result = await db.orders.getRecent(10);
      return result ?? [];
    },
    refetchInterval: 15000,
  });

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 0 }),
    []
  );

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('sk-SK', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    []
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center text-slate-200">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-400 mx-auto mb-4"></div>
          <p className="text-lg text-slate-300">Načítavam štatistiky...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-12">
        <div className="text-error-400 text-6xl mb-4 flex justify-center">
          <WarningIcon className="text-5xl" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-semibold text-primary-100 mb-4">Chyba pri načítaní údajov</h3>
        <button onClick={() => refetch()} className="btn btn-primary">
          <FiRefreshCw className="w-4 h-4 mr-2" />
          Skúsiť znovu
        </button>
      </div>
    );
  }

  const safeStats = {
    total_orders: Number(statsObj.total_orders ?? 0),
    pending_orders: Number(statsObj.pending_orders ?? 0),
    paid_orders: Number(statsObj.paid_orders ?? 0),
    total_amount: Number(statsObj.total_amount ?? 0),
    sold_tickets: Number(statsObj.sold_tickets ?? 0),
  };

  const ticketSummary = {
    total_tickets: Number(ticketStats?.total_tickets ?? eventSettings?.max_tickets ?? 0),
    available_tickets: Number(ticketStats?.available_tickets ?? 0),
    reserved_tickets: Number(ticketStats?.reserved_tickets ?? 0),
    paid_tickets: Number(ticketStats?.paid_tickets ?? safeStats.sold_tickets ?? 0),
  };

  const totalTickets =
    ticketSummary.total_tickets ||
    Number(eventSettings?.max_tickets ?? safeStats.sold_tickets);

  const remainingTickets =
    ticketSummary.available_tickets ||
    Math.max(totalTickets - ticketSummary.paid_tickets, 0);

  const orderedDailyStats = Array.isArray(dailyStats) ? dailyStats : [];
  const todayStats = orderedDailyStats[0] ?? null;
  const previousStats = orderedDailyStats[1] ?? null;

  /**
   * Pomocná funkcia na výpočet percentuálnej zmeny medzi dvoma obdobiami.
   * Vraciame zaokrúhlenú hodnotu, aby bola prezentácia prehľadnejšia.
   */
  const computeChange = (metricKey) => {
    if (!todayStats) return 0;
    const current = Number(todayStats?.[metricKey] ?? 0);
    const previous = Number(previousStats?.[metricKey] ?? 0);
    if (previous === 0 || Number.isNaN(previous)) {
      return current > 0 ? 100 : 0;
    }
    const diff = current - previous;
    return Math.round((diff / previous) * 100);
  };

  const statCards = [
    {
      title: 'Celkové objednávky',
      value: numberFormatter.format(safeStats.total_orders),
      icon: FiUsers,
      variant: 'primary',
      change: computeChange('total_orders'),
    },
    {
      title: 'Čakajúce platby',
      value: numberFormatter.format(safeStats.pending_orders),
      icon: FiClock,
      variant: 'warning',
      change: computeChange('pending_orders'),
    },
    {
      title: 'Zaplatené',
      value: numberFormatter.format(safeStats.paid_orders),
      icon: FiCheck,
      variant: 'success',
      change: computeChange('paid_orders'),
    },
    {
      title: 'Celkový príjem',
      value: currencyFormatter.format(safeStats.total_amount),
      icon: FiDollarSign,
      variant: 'primary',
      change: computeChange('revenue'),
    },
    {
      title: 'Predané lístky',
      value: numberFormatter.format(safeStats.sold_tickets),
      icon: FiTrendingUp,
      variant: 'secondary',
      change: computeChange('tickets_sold'),
    },
    {
      title: 'Zostáva lístkov',
      value: numberFormatter.format(remainingTickets),
      icon: FiTrendingUp,
      variant: 'muted',
      change: -computeChange('tickets_sold'),
    },
  ];

  const getVariantIconClass = (variant) => {
    switch (variant) {
      case 'primary':
        return 'bg-primary-500/20 text-primary-200 border border-primary-500/40';
      case 'success':
        return 'bg-success-500/20 text-success-200 border border-success-500/40';
      case 'warning':
        return 'bg-warning-500/20 text-warning-200 border border-warning-500/40';
      case 'secondary':
        return 'bg-secondary-500/20 text-secondary-200 border border-secondary-500/40';
      default:
        return 'bg-slate-900 text-slate-300 border border-slate-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-primary-100 flex items-center gap-3">
            <ChartIcon className="text-primary-300" aria-hidden="true" />
            <span>Administrácia</span>
          </h1>
          <p className="text-slate-300 mt-1">Prehľad tomboly a štatistiky</p>
        </div>
        <button
          onClick={async () => {
            // Obnoviť všetky queries na dashboarde
            await Promise.all([
              refetch(),
              queryClient.refetchQueries({ queryKey: ['event-settings'] }),
              queryClient.refetchQueries({ queryKey: ['ticket-stats'] }),
              queryClient.refetchQueries({ queryKey: ['daily-stats'] }),
              queryClient.refetchQueries({ queryKey: ['recent-orders'] }),
            ]);
          }}
          className="btn btn-secondary flex items-center gap-2"
        >
          <FiRefreshCw className="w-4 h-4" />
          Obnoviť
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card hover:shadow-ticket-hover transition-shadow backdrop-blur-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${getVariantIconClass(stat.variant)}`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <span
                className={`text-sm font-medium px-2 py-1 rounded-full border ${
                  stat.change >= 0
                    ? 'bg-success-500/15 text-success-200 border-success-500/40'
                    : 'bg-error-500/15 text-error-200 border-error-500/40'
                }`}
              >
                {stat.change >= 0 ? `+${stat.change}%` : `${stat.change}%`}
              </span>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">{stat.title}</p>
              <p className="text-2xl font-bold text-slate-100">{stat.value}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Progress Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        className="card backdrop-blur-sm"
        >
        <h3 className="text-xl font-semibold text-primary-100 mb-6 flex items-center gap-2">
          <GrowthIcon className="text-primary-300" aria-hidden="true" />
          <span>Pokrok predaja</span>
        </h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
              <span className="text-sm text-slate-400">Predané lístky</span>
              <span className="text-sm font-semibold text-slate-100">
                  {numberFormatter.format(safeStats.sold_tickets)}/{numberFormatter.format(totalTickets)}
                </span>
              </div>
            <div className="w-full bg-slate-800 rounded-full h-3">
                <div 
                className="bg-primary-500 h-3 rounded-full transition-all duration-500"
                  style={{
                    width: `${
                      totalTickets
                        ? Math.min((safeStats.sold_tickets / totalTickets) * 100, 100)
                        : 0
                    }%`,
                  }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
              <span className="text-sm text-slate-400">Zaplatené objednávky</span>
              <span className="text-sm font-semibold text-slate-100">
                  {numberFormatter.format(safeStats.paid_orders)}/{numberFormatter.format(safeStats.total_orders)}
                </span>
              </div>
            <div className="w-full bg-slate-800 rounded-full h-3">
                <div 
                className="bg-success-500 h-3 rounded-full transition-all duration-500"
                  style={{
                    width: `${
                      safeStats.total_orders
                        ? Math.min((safeStats.paid_orders / safeStats.total_orders) * 100, 100)
                        : 0
                    }%`,
                  }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
              <span className="text-sm text-slate-400">Cieľový príjem</span>
              <span className="text-sm font-semibold text-slate-100">
                  {currencyFormatter.format(safeStats.total_amount)}/
                  {currencyFormatter.format(
                    Number(eventSettings?.max_tickets ?? totalTickets) *
                      Number(eventSettings?.ticket_price ?? 5)
                  )}
                </span>
              </div>
            <div className="w-full bg-slate-800 rounded-full h-3">
                <div 
                className="bg-secondary-500 h-3 rounded-full transition-all duration-500"
                  style={{
                    width: `${
                      eventSettings?.ticket_price
                        ? Math.min(
                            (safeStats.total_amount /
                              (Number(eventSettings.ticket_price) * totalTickets || 1)) * 100,
                            100
                          )
                        : 0
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        className="card backdrop-blur-sm"
        >
        <h3 className="text-xl font-semibold text-primary-100 mb-6 flex items-center gap-2">
          <TimeIcon className="text-primary-300" aria-hidden="true" />
          <span>Posledné objednávky</span>
        </h3>
          
          <div className="space-y-3">
            {recentOrders?.length > 0 ? (
              recentOrders.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-3 bg-slate-900/60 border border-slate-800 rounded-lg">
                  <div>
                  <p className="font-medium text-slate-100">
                      {order.first_name} {order.last_name}
                    </p>
                  <p className="text-sm text-slate-400">
                      {numberFormatter.format(Number(order.ticket_count ?? order.tickets?.length ?? 0))} lístkov •{' '}
                      {currencyFormatter.format(Number(order.total_amount ?? 0))}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      order.status === 'paid' 
                      ? 'bg-success-500/20 text-success-200 border border-success-500/40' 
                        : order.status === 'pending'
                        ? 'bg-warning-500/20 text-warning-200 border border-warning-500/40'
                        : 'bg-error-500/20 text-error-200 border border-error-500/40'
                    }`}>
                      {order.status === 'paid' ? 'Zaplatené' : 
                       order.status === 'pending' ? 'Čaká' : 'Zrušené'}
                    </span>
                  <p className="text-xs text-slate-500 mt-1">
                      {order.created_at
                        ? `${new Date(order.created_at).toLocaleDateString('sk')} ${new Date(
                            order.created_at
                          ).toLocaleTimeString('sk')}`
                        : '—'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
            <p className="text-center text-slate-500 py-6">Žiadne objednávky</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-600 rounded-2xl p-6 text-slate-950 shadow-glow"
      >
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <LightningIcon className="text-slate-900" aria-hidden="true" />
          <span>Rýchle akcie</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button className="bg-black/10 backdrop-blur rounded-xl p-4 hover:bg-black/15 transition-all">
            <div className="text-2xl mb-2 flex justify-center">
              <MoneyIcon aria-hidden="true" />
            </div>
            <div className="text-sm font-medium">Správa platieb</div>
          </button>
          <button className="bg-black/10 backdrop-blur rounded-xl p-4 hover:bg-black/15 transition-all">
            <div className="text-2xl mb-2 flex justify-center">
              <BullseyeIcon aria-hidden="true" />
            </div>
            <div className="text-sm font-medium">Spustiť žrebovanie</div>
          </button>
          <button className="bg-black/10 backdrop-blur rounded-xl p-4 hover:bg-black/15 transition-all">
            <div className="text-2xl mb-2 flex justify-center">
              <ChartIcon aria-hidden="true" />
            </div>
            <div className="text-sm font-medium">Export reportu</div>
          </button>
          <button className="bg-black/10 backdrop-blur rounded-xl p-4 hover:bg-black/15 transition-all">
            <div className="text-2xl mb-2 flex justify-center">
              <SettingsGearIcon aria-hidden="true" />
            </div>
            <div className="text-sm font-medium">Nastavenia</div>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;